import { moviesVideos } from "@/mocks/videosMovie";
import { tvVideos } from "@/mocks/videosTv";
import { varietyVideos } from "@/mocks/videosVariety";
import type { SortValue, Video, VideoType } from "@/types/video";

export const allVideos: Video[] = [
  ...moviesVideos,
  ...tvVideos,
  ...varietyVideos,
];

export const PAGE_SIZE = 24;

export function getVideosByType(type: VideoType | string): Video[] {
  return allVideos.filter((video) => video.type === type);
}

export function getVideoById(id: string): Video | undefined {
  return allVideos.find((video) => video.id === id);
}

export function getLatestVideos(count: number): Video[] {
  return [...allVideos]
    .sort(
      (a, b) =>
        new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime(),
    )
    .slice(0, count);
}

export function getHotVideos(count: number): Video[] {
  return [...allVideos].sort((a, b) => b.views - a.views).slice(0, count);
}

export function getRelatedVideos(video: Video, count: number): Video[] {
  const sameType = allVideos.filter(
    (item) => item.id !== video.id && item.type === video.type,
  );
  const tagMatched = sameType.filter((item) =>
    item.tags.some((tag) => video.tags.includes(tag)),
  );
  const rest = sameType.filter((item) => !tagMatched.includes(item));
  return [...tagMatched, ...rest]
    .sort((a, b) => b.views - a.views)
    .slice(0, count);
}

export function searchVideos(keyword: string): Video[] {
  const trimmed = keyword.trim().toLowerCase();
  if (!trimmed) {
    return [];
  }
  return allVideos.filter(
    (video) =>
      video.title.toLowerCase().includes(trimmed) ||
      video.tags.some((tag) => tag.toLowerCase().includes(trimmed)) ||
      video.actors.some((actor) => actor.toLowerCase().includes(trimmed)),
  );
}

export interface FilterParams {
  region: string;
  year: string;
  sort: SortValue;
}

export function filterVideos(
  videos: Video[],
  { region, year, sort }: FilterParams,
): Video[] {
  let result = [...videos];

  if (region && region !== "全部") {
    result = result.filter((video) => video.region === region);
  }

  if (year && year !== "all") {
    const currentYear = new Date().getFullYear();
    if (year === "recent1") {
      result = result.filter((video) => currentYear - video.year <= 1);
    } else if (year === "recent5") {
      result = result.filter((video) => currentYear - video.year <= 5);
    } else if (year === "classic") {
      result = result.filter((video) => currentYear - video.year > 5);
    }
  }

  if (sort === "hot") {
    result.sort((a, b) => b.views - a.views);
  } else if (sort === "rating") {
    result.sort((a, b) => b.rating - a.rating);
  } else {
    result.sort(
      (a, b) =>
        new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime(),
    );
  }

  return result;
}

export function paginate<T>(items: T[], page: number, pageSize = PAGE_SIZE) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    pageItems: items.slice(start, start + pageSize),
    total,
    totalPages,
    page: safePage,
  };
}

export function formatViews(views: number): string {
  if (views >= 100000000) {
    return `${(views / 100000000).toFixed(1)}亿`;
  }
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return String(views);
}

export function getTypeName(type: VideoType): string {
  if (type === "movie") return "电影";
  if (type === "tv") return "电视剧";
  return "综艺";
}

export function getTypePath(type: VideoType): string {
  return `/category/${type}`;
}